import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import {
  AUTO_BIDDING_FREQUENCIES,
  AUTO_BIDDING_STRATEGIES,
  buildAutoBiddingSavePayload,
  parseAutoBiddingSettings,
  validateAutoBiddingForm,
  type AutoBiddingFrequency,
  type AutoBiddingSavePayload,
  type AutoBiddingSettings,
  type AutoBiddingStrategy,
} from '@/utils/autoBidding';

export function AutoBiddingModal({
  campaignName,
  initial,
  loading,
  saving,
  disabling,
  error,
  onClose,
  onSave,
  onDisable,
}: {
  campaignName: string;
  initial: AutoBiddingSettings | null;
  loading: boolean;
  saving: boolean;
  disabling: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (payload: AutoBiddingSavePayload) => void;
  onDisable?: () => void;
}) {
  const [strategy, setStrategy] = useState<AutoBiddingStrategy | null>(null);
  const [targetAcos, setTargetAcos] = useState('');
  const [biddingFrequency, setBiddingFrequency] = useState<AutoBiddingFrequency | ''>('');
  const [maxBid, setMaxBid] = useState('');
  const [autoAddTargets, setAutoAddTargets] = useState(false);
  const [reduceOutOfBudget, setReduceOutOfBudget] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    const settings = initial ? parseAutoBiddingSettings(initial) : null;
    setStrategy(settings?.strategy ?? null);
    setTargetAcos(settings?.targetAcos != null ? String(settings.targetAcos) : '');
    setBiddingFrequency(settings?.biddingFrequency ?? '');
    setMaxBid(settings?.maxBid != null ? String(settings.maxBid) : '');
    setAutoAddTargets(settings?.autoAddTargets ?? false);
    setReduceOutOfBudget(settings?.reduceOutOfBudget ?? false);
    setFormError(null);
  }, [initial]);

  const busy = saving || disabling || loading;
  const canDisable = Boolean(initial?.enabled && onDisable);

  function handleSave() {
    const message = validateAutoBiddingForm({
      strategy,
      targetAcos,
      biddingFrequency,
      maxBid,
    });
    if (message || !strategy || !biddingFrequency) {
      setFormError(message);
      return;
    }
    setFormError(null);
    onSave(
      buildAutoBiddingSavePayload({
        strategy,
        targetAcos,
        biddingFrequency,
        maxBid,
        autoAddTargets,
        reduceOutOfBudget,
      })
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex w-full max-w-lg flex-col rounded-xl bg-surface p-5 shadow-xl">
        <button
          type="button"
          className="absolute right-4 top-4 rounded-md p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="pr-8 text-base font-semibold text-foreground">Auto Bidding</h3>
        <p className="mt-1 text-sm text-foreground-muted">{campaignName}</p>
        <div className="mt-4 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading settings…
            </div>
          ) : (
            <>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-foreground">Strategy</legend>
                <div className="grid gap-2">
                  {AUTO_BIDDING_STRATEGIES.map((option) => {
                    const selected = strategy === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        disabled={busy}
                        onClick={() => setStrategy(option.id)}
                        className={cn(
                          'rounded-lg border px-3 py-2 text-left transition-colors',
                          selected
                            ? 'border-brand bg-brand/5'
                            : 'border-border hover:border-brand'
                        )}
                      >
                        <div className="text-sm font-medium text-foreground">{option.label}</div>
                        <div className="mt-0.5 text-xs text-foreground-muted">{option.description}</div>
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-medium text-foreground sm:col-span-1">
                  Target ACOS (%)
                  <Input
                    className="mt-1"
                    type="number"
                    min="0.01"
                    step="0.1"
                    required
                    value={targetAcos}
                    disabled={busy}
                    onChange={(e) => setTargetAcos(e.target.value)}
                  />
                </label>
                <label className="text-sm font-medium text-foreground">
                  Bidding frequency
                  <Select
                    className="mt-1 w-full"
                    required
                    value={biddingFrequency}
                    disabled={busy}
                    onChange={(e) =>
                      setBiddingFrequency(e.target.value as AutoBiddingFrequency | '')
                    }
                  >
                    <option value="">Select frequency</option>
                    {AUTO_BIDDING_FREQUENCIES.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </label>
                <label className="text-sm font-medium text-foreground sm:col-span-2">
                  Max Bid (optional)
                  <Input
                    className="mt-1"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={maxBid}
                    disabled={busy}
                    onChange={(e) => setMaxBid(e.target.value)}
                  />
                </label>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={autoAddTargets}
                    disabled={busy}
                    onChange={(e) => setAutoAddTargets(e.target.checked)}
                  />
                  Automatically add targets
                </label>
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={reduceOutOfBudget}
                    disabled={busy}
                    onChange={(e) => setReduceOutOfBudget(e.target.checked)}
                  />
                  Reduce Campaign Out of budget
                </label>
              </div>
            </>
          )}

          {formError || error ? (
            <p className="text-sm text-error">{formError || error}</p>
          ) : null}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {canDisable ? (
              <Button variant="secondary" disabled={busy} onClick={onDisable}>
                {disabling ? 'Disabling…' : 'Disable'}
              </Button>
            ) : null}
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={busy || loading} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
